import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App,
  Button,
  Flex,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type { TablePaginationConfig, TableProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { AxiosError } from 'axios'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  accountSessionsDestroyMutation,
  accountSessionsListOptions,
  accountSessionsListQueryKey,
  accountSessionsRevokeAllOthersDestroyMutation,
  type Session
} from 'src/api/main'
import {
  DateTimeCell,
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import { LOGOUT_ENFORCED_KEY } from 'src/constants'
import { DEFAULT_PER_PAGE_OPTIONS, useListQueryState } from 'src/hooks'
import { paths } from 'src/router/paths'
import { clearLocalStorage, useShowApiErrors } from 'src/utils'

const { Text } = Typography

const formatLocation = (row: Session): string => {
  if (row.city && row.country_code) return `${row.city}, ${row.country_code}`
  if (row.country_code) return row.country_code
  if (row.city) return row.city
  return row.ip_address || '—'
}

export const AccountSessionsPanel = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Own URL keys (not the generic `page`/`per_page`) so pagination doesn't
  // collide with the sibling audit panel, which shares this route's query
  // string. No filters here, so the `page`-key auto-reset isn't needed.
  const list = useListQueryState({
    sessions_page: { type: 'number', default: 1 },
    sessions_per_page: {
      type: 'number',
      default: 25,
      allowed: DEFAULT_PER_PAGE_OPTIONS
    }
  })
  const page = list.values.sessions_page
  const perPage = list.values.sessions_per_page

  const sessionsQuery = useQuery(
    accountSessionsListOptions({ query: { page, per_page: perPage } })
  )

  const listQueryKey = accountSessionsListQueryKey()

  // Revoking shifts every row after the deleted one, so the other cached pages
  // are wrong. `refetchOnMount` is off app-wide, so marking them stale would
  // leave them on screen until a hard reload — drop them and let the page the
  // user opens next fetch itself. Refetching them all here instead would fire
  // one request per cached page, including pages that no longer exist.
  const showSessionsPage = (nextPage: number) => {
    queryClient.removeQueries({ queryKey: listQueryKey, type: 'inactive' })

    if (nextPage !== page) {
      list.set('sessions_page', nextPage)

      return
    }

    return queryClient.refetchQueries({
      queryKey: listQueryKey,
      type: 'active'
    })
  }

  // Landing on a page the list no longer has (a stale link, or the last row of
  // the last page revoked elsewhere) answers 404 — the table would render empty
  // with no pagination left to click back with.
  const isSessionsPageMissing =
    page > 1 &&
    (sessionsQuery.error as AxiosError | null)?.response?.status === 404

  useEffect(() => {
    if (isSessionsPageMissing) {
      list.set('sessions_page', 1)
    }
  }, [isSessionsPageMissing, list])

  const signOutLocally = () => {
    // Server already blacklisted our session — just tear down the client state
    // and send the user to /login. Calling the logout endpoint would 401 here.
    clearLocalStorage()
    queryClient.clear()
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(LOGOUT_ENFORCED_KEY, '1')
    }
    navigate(paths.auth.login, { replace: true })
  }

  const { mutateAsync: revokeOne, isPending: isRevokingOne } = useMutation({
    ...accountSessionsDestroyMutation(),
    onSuccess: async (_data, variables) => {
      message.success(t('account.sessions.messages.revoked'))
      const revokedJti = variables.path.jti
      const rows = sessionsQuery.data?.results ?? []
      const revokedRow = rows.find((s) => s.id === revokedJti)
      if (revokedRow?.is_current) {
        signOutLocally()

        return
      }
      await showSessionsPage(rows.length === 1 ? Math.max(1, page - 1) : page)
    },
    onError: (e) => showApiErrors(e)
  })

  const { mutateAsync: revokeAllOthers, isPending: isRevokingAll } =
    useMutation({
      ...accountSessionsRevokeAllOthersDestroyMutation(),
      onSuccess: async () => {
        message.success(t('account.sessions.messages.revokedAll'))
        await showSessionsPage(1)
      },
      onError: (e) => showApiErrors(e)
    })

  const sessions = sessionsQuery.data?.results ?? []
  const total = sessionsQuery.data?.count ?? 0
  const hasOthers = sessions.some((s) => !s.is_current)

  const handleTableChange: NonNullable<TableProps<Session>['onChange']> = (
    pagination
  ) => {
    const nextPerPage = pagination.pageSize ?? perPage
    // Changing page size resets to the first page so we never land on an
    // out-of-range page.
    if (nextPerPage !== perPage) {
      list.setMany({ sessions_per_page: nextPerPage, sessions_page: 1 })
    } else {
      list.set('sessions_page', pagination.current ?? 1)
    }
  }

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize: perPage,
    total,
    pageSizeOptions: DEFAULT_PER_PAGE_OPTIONS.map(String),
    showSizeChanger: { showSearch: false },
    size: 'small',
    hideOnSinglePage: true,
    showTotal: (count, [from, to]) =>
      t('pagination.range', {
        from,
        to,
        total: count,
        defaultValue: `${from}-${to} of ${count}`
      })
  }

  const columns: ColumnsType<Session> = [
    {
      title: t('account.sessions.columns.device'),
      key: 'device',
      fixed: 'left',
      render: (_, row) => {
        // OIDC sessions belong to a third-party app — surface its name as the
        // primary label so the user can tell which integration this is.
        const isOidc = row.kind === 'oidc'
        const primary = isOidc
          ? row.application?.name || row.device_name || '—'
          : row.device_name || '—'
        const secondary = isOidc
          ? row.device_name || row.user_agent
          : row.user_agent

        return (
          <Space vertical size={0}>
            <Text strong>{primary}</Text>
            {secondary ? (
              <Text type="secondary" ellipsis style={{ maxWidth: 360 }}>
                {secondary}
              </Text>
            ) : null}
          </Space>
        )
      }
    },
    {
      title: t('account.sessions.columns.workspace'),
      key: 'workspace',
      render: (_, row) =>
        row.organization?.name ? (
          <Tag color="geekblue">{row.organization.name}</Tag>
        ) : (
          <Text type="secondary">{t('account.sessions.noWorkspace')}</Text>
        )
    },
    {
      title: t('account.sessions.columns.location'),
      key: 'location',
      render: (_, row) => (
        <Tooltip title={row.ip_address || ''}>
          <Text>{formatLocation(row)}</Text>
        </Tooltip>
      )
    },
    {
      title: t('account.sessions.columns.lastActive'),
      key: 'lastActive',
      render: (_, row) => (
        <DateTimeCell
          value={row.last_used_at || row.created_at}
          variant="dateTime"
        />
      )
    },
    {
      title: t('account.sessions.columns.status'),
      key: 'status',
      render: (_, row) => {
        // Auth method attributes the session to the login flow used
        // (password, google, etc.). OIDC third-party sessions leave this null
        // by design — ``kind === 'oidc'`` plus the application name already
        // identify them uniquely.
        const authMethodKey = row.auth_method
          ? `account.sessions.authMethod.${row.auth_method}`
          : null

        return (
          <Space size={4} wrap>
            {row.is_current ? (
              <Tag color="success">{t('account.sessions.current')}</Tag>
            ) : (
              <Tag>{t('account.sessions.active')}</Tag>
            )}
            {row.kind === 'oidc' ? (
              <Tag color="blue">{t('account.sessions.kind.oidc')}</Tag>
            ) : null}
            {authMethodKey ? <Tag>{t(authMethodKey)}</Tag> : null}
          </Space>
        )
      }
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      align: 'right',
      render: (_, row) => (
        <Popconfirm
          title={
            row.kind === 'oidc' && row.application?.name
              ? t('account.sessions.revokeOidcConfirm', {
                  name: row.application.name
                })
              : row.is_current
                ? t('account.sessions.revokeCurrentConfirm')
                : t('account.sessions.revokeConfirm')
          }
          okButtonProps={{ loading: isRevokingOne, danger: true }}
          onConfirm={() => void revokeOne({ path: { jti: row.id } })}
        >
          <Button type="link" danger size="small">
            {t('account.sessions.revoke')}
          </Button>
        </Popconfirm>
      )
    }
  ]

  return (
    <Flex vertical gap={14}>
      {sessionsQuery.isError ? (
        <QueryErrorAlert onRetry={() => void sessionsQuery.refetch()} />
      ) : null}

      <ProTableContainer<Session>
        showSearch={false}
        rowKey="id"
        loading={sessionsQuery.isLoading}
        dataSource={sessions}
        columns={columns}
        pagination={pagination}
        onChange={handleTableChange}
        locale={{ emptyText: t('account.sessions.empty') }}
        leftChildren={<Text strong>{t('account.section.sessions')}</Text>}
        rightChildren={
          <Popconfirm
            title={t('account.sessions.revokeAllOtherConfirm')}
            okButtonProps={{ loading: isRevokingAll }}
            disabled={!hasOthers}
            onConfirm={() => void revokeAllOthers({})}
          >
            <Button
              size="small"
              danger
              disabled={!hasOthers}
              loading={isRevokingAll}
            >
              {t('account.sessions.revokeAllOther')}
            </Button>
          </Popconfirm>
        }
      />
    </Flex>
  )
}
